import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Ruler, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

/**
 * CartFitSuggestionBanner — second-chance nudge after the cart loads.
 * Shown ONLY when the customer has saved measurements AND there's at least one
 * standard-size cart line on a stitching-enabled product.
 *
 * One tap on "Apply" PATCHes those items in-place to use the saved profile.
 */
export default function CartFitSuggestionBanner({ onApplied }) {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/cart/fit-suggestions");
        if (alive) setSuggestions(r.data?.suggestions || []);
      } catch {
        /* silent — banner just doesn't render */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.post("/cart/apply-fit-suggestions", {});
      toast.success(`Applied to ${r.data.applied} item${r.data.applied === 1 ? "" : "s"}`);
      setSuggestions([]);
      if (onApplied) await onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply");
    } finally {
      setApplying(false);
    }
  };

  if (loading || !user || dismissed || suggestions.length === 0) return null;

  // Group by profile for a cleaner copy line
  const byProfile = {};
  for (const s of suggestions) {
    byProfile[s.profile_id] ??= { name: s.profile_name, count: 0, ctype: s.clothing_type_name, isDefault: s.is_profile_default };
    byProfile[s.profile_id].count += 1;
  }
  const headline = Object.values(byProfile)
    .map((p) => `${p.count} ${p.ctype.toLowerCase()} item${p.count === 1 ? "" : "s"} · use ${p.name}${p.isDefault ? " (default)" : ""}`)
    .join(" • ");

  return (
    <div
      className="bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-primary))]/20 p-4 md:p-5 mb-6 flex items-start gap-3"
      data-testid="fit-suggestion-banner"
    >
      <div className="p-2 bg-white border border-[hsl(var(--kb-primary))]/20 shrink-0">
        <Sparkles size={16} className="text-[hsl(var(--kb-primary))]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="overline mb-1 flex items-center gap-1">
          <Ruler size={11} /> Better fit available
        </div>
        <div className="font-display text-base md:text-lg leading-snug mb-1">
          We can apply your saved measurements to{" "}
          {suggestions.length} item{suggestions.length === 1 ? "" : "s"} in your bag.
        </div>
        <div className="text-xs text-[hsl(var(--kb-muted))]">{headline}</div>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        <button
          onClick={apply}
          disabled={applying}
          className="kb-btn-primary !py-2 !px-4 !text-xs disabled:opacity-60"
          data-testid="fit-suggestion-apply-btn"
          type="button"
        >
          {applying ? "Applying…" : "Apply"}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="text-[10px] text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))] flex items-center gap-1"
          data-testid="fit-suggestion-dismiss"
          type="button"
        >
          <X size={10} /> Not now
        </button>
      </div>
    </div>
  );
}
